import { useMutation } from '@apollo/client';
import {
  LOCK_ARTICLE,
  RETAIN_LOCK_ARTICLE,
  UNLOCK_ARTICLE,
} from '../../../apollo/mutations/articleLock.web.graphql';

/**
 * Custom hook for managing concurrential access to articles
 * @returns Object with methods for locking, unlocking, and retaining article locks
 */
export const useArticleConcurrentialAccess = () => {
  const [lockArticleMutation] = useMutation(LOCK_ARTICLE);
  const [unlockArticleMutation] = useMutation(UNLOCK_ARTICLE);
  const [retainLockArticleMutation] = useMutation(RETAIN_LOCK_ARTICLE);

  /**
   * Locks an article for editing by the current user
   * @param articleId The ID of the article to lock
   * @returns A promise that resolves when the lock is applied
   */
  const lockArticle = async (articleId: string): Promise<void> => {
    await lockArticleMutation({
      variables: {
        where: { id: articleId },
      },
    });
  };

  /**
   * Unlocks an article, releasing it for other users to edit
   * @param articleId The ID of the article to unlock
   * @returns A promise that resolves when the unlock is applied
   */
  const unlockArticle = async (articleId: string): Promise<void> => {
    await unlockArticleMutation({
      variables: {
        where: { id: articleId },
      },
    });
  };

  /**
   * Retains the lock on an article for the current user
   * @param articleId The ID of the article to retain the lock for
   * @returns A promise that resolves when the lock is retained
   */
  const retainArticleLock = async (articleId: string): Promise<void> => {
    await retainLockArticleMutation({
      variables: {
        where: { id: articleId },
      },
      fetchPolicy: 'no-cache',
    });
  };

  return {
    lockArticle,
    unlockArticle,
    retainArticleLock,
  };
};
